import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { OrderCryptoDTO } from '@libs/dtos/order/order-request.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { CreateCryptoOrderResponse, PaymentsService } from '@services/payments/payments.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import type {
	CryptoPaymentMethod,
	ManualPaymentMethod,
} from '@shared/components/payment-method-modal/payment-method-modal.component';
import { translateApiError } from 'app/core/utils/api-error';
import { firstValueFrom } from 'rxjs';

/** Order as the Boost form builds it; the backend recomputes the price from it */
export interface CheckoutOrder {
	userId: string;
	platformId: string;
	productId: string;
	amount: number;
	url: string;
	customComments?: string;
	promoCode?: string;
	queueIfLinkBusy?: boolean;
}

/** Crypto method ids of the payment modal → currency the backend expects */
const CRYPTO_CURRENCIES: Record<CryptoPaymentMethod, OrderCryptoDTO['cryptoCurrency']> = {
	crypto_btc: 'BTC',
	crypto_usdt_trc20: 'USDT_TRC20',
	crypto_usdt_erc20: 'USDT_ERC20',
};

/**
 * Order creation of the Boost page, one flow per payment path:
 * - direct (admin, no payment): POST /orders/direct, with the "link busy → queue" confirmation;
 * - card: Stripe checkout session, then redirect;
 * - Revolut / Bizum: pending order, the user confirms the transfer later;
 * - crypto: pending order with the wallet instructions.
 * It shows the result messages; what the page does afterwards (reset the form, close the modal,
 * navigate) comes back through the callbacks. Provided by the page.
 */
@Injectable()
export class OrderCheckoutService {
	private readonly paymentsService = inject(PaymentsService);
	private readonly messageAlertService = inject(MessageAlertService);
	private readonly confirmDialogService = inject(ConfirmDialogService);
	private readonly translocoService = inject(TranslocoService);

	/** Direct or card order being created (submit button busy) */
	creatingOrder = false;
	/** Pending (manual / crypto) order being created: the modal shows it busy */
	isCreatingManualOrder = false;
	/** Order waiting for the user to pick a payment method in the modal */
	pendingOrderData: CheckoutOrder | null = null;
	/** Pending order already created (manual / crypto), waiting for the payment */
	pendingOrderId: number | null = null;

	/** Modal closed or order finished: nothing pending any more */
	resetPaymentState(): void {
		this.pendingOrderData = null;
		this.pendingOrderId = null;
		this.isCreatingManualOrder = false;
	}

	/** Admin / Root: the order is created and sent at once (no payment) */
	async createDirect(orderData: CheckoutOrder, onCreated: () => void): Promise<void> {
		this.creatingOrder = true;

		try {
			const response = await firstValueFrom(this.paymentsService.createOrderDirect(orderData));

			// Another order of this service is running on the link: cancel or queue it behind
			if (response?.linkBusy) {
				this.confirmDialogService.confirmCustom({
					header: this.translocoService.translate('boost.messages.linkBusyTitle'),
					message: response.message,
					icon: 'Clock',
					acceptLabel: this.translocoService.translate('boost.messages.linkBusyQueue'),
					rejectLabel: this.translocoService.translate('boost.messages.linkBusyCancel'),
					accept: () => this.createDirect({ ...orderData, queueIfLinkBusy: true }, onCreated),
				});
				return;
			}

			if (response?.success) {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('boost.messages.orderCreatedTitle'),
					this.translocoService.translate('boost.messages.orderCreated', {
						orderId: response.orderId,
						message: response.message,
					}),
				);
				onCreated();
			} else {
				// The order exists but the provider did not take it (reason in the message)
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					response?.message || this.translocoService.translate('boost.messages.orderError'),
				);
			}
		} catch (error: unknown) {
			console.error('Error creating order:', error);
			this.showCreateError(error);
		} finally {
			this.creatingOrder = false;
		}
	}

	/** Card: Stripe checkout session, then the browser goes to Stripe */
	async payByCard(orderData: CheckoutOrder): Promise<void> {
		this.creatingOrder = true;

		try {
			const response = await firstValueFrom(this.paymentsService.createOrderCheckout(orderData));

			if (response?.checkoutUrl) {
				this.paymentsService.redirectToCheckout(response.checkoutUrl);
			} else {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('boost.messages.paymentUrlError'),
				);
			}
		} catch (error: unknown) {
			console.error('Error creating order:', error);
			this.showCreateError(error);
		} finally {
			this.creatingOrder = false;
		}
	}

	/**
	 * Crypto: pending order of the order waiting in the modal. A total of 0 is already paid (`onFree`);
	 * otherwise `onInstructions` gets the wallet details to show.
	 */
	async createCrypto(
		method: CryptoPaymentMethod,
		onFree: (orderId: number) => void,
		onInstructions: (response: CreateCryptoOrderResponse) => void,
	): Promise<void> {
		if (!this.pendingOrderData || this.isCreatingManualOrder) return; // already creating it

		this.isCreatingManualOrder = true;

		try {
			const response = await firstValueFrom(
				this.paymentsService.createCryptoOrder({
					...this.pendingOrderData,
					cryptoCurrency: CRYPTO_CURRENCIES[method],
				}),
			);

			if (response?.success && response.paid) {
				onFree(response.orderId);
			} else if (response?.success) {
				this.pendingOrderId = response.orderId;
				onInstructions(response);
			} else {
				this.showOrderError();
			}
		} catch (error: unknown) {
			console.error('Error creating crypto order:', error);
			this.showCreateError(error);
		} finally {
			this.isCreatingManualOrder = false;
		}
	}

	/** Revolut / Bizum: pending order of the order waiting in the modal (a total of 0 is already paid) */
	async createPending(method: ManualPaymentMethod, onFree: (orderId: number) => void): Promise<void> {
		if (!this.pendingOrderData || this.isCreatingManualOrder) return; // already creating it

		this.isCreatingManualOrder = true;

		try {
			const response = await firstValueFrom(
				this.paymentsService.createPendingOrder({
					...this.pendingOrderData,
					paymentMethod: method,
				}),
			);

			if (response?.success && response.paid) {
				onFree(response.orderId);
			} else if (response?.success) {
				this.pendingOrderId = response.orderId;
			} else {
				this.showOrderError();
			}
		} catch (error: unknown) {
			console.error('Error creating pending order:', error);
			this.showCreateError(error);
		} finally {
			this.isCreatingManualOrder = false;
		}
	}

	/** The user says the transfer is done */
	async confirmManualPayment(orderId: number, onConfirmed: () => void): Promise<void> {
		try {
			const response = await firstValueFrom(this.paymentsService.confirmPayment(orderId));

			if (response?.success) {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('boost.messages.paymentConfirmedTitle'),
					this.translocoService.translate('boost.messages.paymentConfirmed', { orderId }),
				);
				onConfirmed();
			} else {
				this.showConfirmError();
			}
		} catch (error) {
			console.error('Error confirming payment:', error);
			this.showConfirmError();
		}
	}

	/** Modal closed: a pending order already created stays saved (the user is told), then nothing is pending */
	cancel(): void {
		if (this.pendingOrderId) {
			this.messageAlertService.showMessage(
				'info',
				this.translocoService.translate('boost.messages.orderSavedTitle'),
				this.translocoService.translate('boost.messages.orderSaved', { orderId: this.pendingOrderId }),
			);
		}
		this.resetPaymentState();
	}

	private showOrderError(): void {
		this.messageAlertService.showMessage(
			'error',
			this.translocoService.translate('common.labels.error'),
			this.translocoService.translate('boost.messages.orderError'),
		);
	}

	private showCreateError(error: unknown): void {
		this.messageAlertService.showMessage(
			'error',
			this.translocoService.translate('common.labels.error'),
			translateApiError(error, this.translocoService, 'boost.messages.orderCreateError'),
		);
	}

	private showConfirmError(): void {
		this.messageAlertService.showMessage(
			'error',
			this.translocoService.translate('common.labels.error'),
			this.translocoService.translate('boost.messages.paymentConfirmError'),
		);
	}
}
